import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Snackbar,
  TableCell,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Format a nullable coordinate value to 4 decimal places, or '-' when absent
export const formatCoord4 = (value: number | null | undefined): string =>
  value == null ? '-' : Number(value).toFixed(4);

// Format a nullable coordinate value to 2 decimal places, or '-' when absent
export const formatCoord2 = (value: number | string | null | undefined): string =>
  value == null ? '-' : Number(value).toFixed(2);

// ── Shared hooks ─────────────────────────────────────────────────────────────

/** Manages page + rowsPerPage state and their event handlers. */
export function usePaginationState(initialRowsPerPage = 20) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  return { page, setPage, rowsPerPage, handleChangePage, handleChangeRowsPerPage };
}

/** How a submitted search term is matched against the reference data. */
export type SearchMatchMode = 'exact' | 'contains';

/**
 * Search state for the Data Management lists.
 *
 * The typed value is kept locally and only applied to the query when the user
 * submits it (Enter or the Search button) — nothing is fetched while typing.
 * Matching defaults to `exact`, which is what these reference lookups
 * (postal code, IATA, UN/LOCODE, city, address) almost always want; `contains`
 * is the opt-in wildcard mode.
 */
export function useReferenceSearch(setPage: (p: number) => void) {
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [matchMode, setMatchMode] = useState<SearchMatchMode>('exact');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const submitSearch = () => {
    setSearch(inputValue.trim());
    setPage(0);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitSearch();
    }
  };

  const clearSearch = () => {
    setInputValue('');
    setSearch('');
    setPage(0);
  };

  const handleMatchModeChange = (mode: SearchMatchMode) => {
    setMatchMode(mode);
    setPage(0);
  };

  return {
    /** The applied term — send this to the API. */
    search,
    matchMode,
    /** Spread straight into <SearchToolbar> / <SearchBarPaper>. */
    searchProps: {
      inputValue,
      onInputChange: handleInputChange,
      onKeyDown: handleSearchKeyDown,
      onSubmit: submitSearch,
      onClear: clearSearch,
      matchMode,
      onMatchModeChange: handleMatchModeChange,
      activeSearch: search,
      hasPendingInput: inputValue.trim() !== search,
    },
  };
}

// ── Shared JSX components ────────────────────────────────────────────────────

interface StatusSnackbarsProps {
  error: string;
  success: string;
  onClearError: () => void;
  onClearSuccess: () => void;
}

/** Renders the error + success Snackbar/Alert pair used by every page. */
export const StatusSnackbars: React.FC<StatusSnackbarsProps> = ({
  error,
  success,
  onClearError,
  onClearSuccess,
}) => (
  <>
    <Snackbar
      open={!!error}
      autoHideDuration={6000}
      onClose={onClearError}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: 99999 }}
    >
      <Alert onClose={onClearError} severity="error" sx={{ width: '100%' }}>
        {error}
      </Alert>
    </Snackbar>
    <Snackbar
      open={!!success}
      autoHideDuration={6000}
      onClose={onClearSuccess}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: 99999 }}
    >
      <Alert onClose={onClearSuccess} severity="success" sx={{ width: '100%' }}>
        {success}
      </Alert>
    </Snackbar>
  </>
);

interface CountChipProps {
  loading: boolean;
  count: number;
  label: string;
}

/** The styled count chip (e.g. "1 234 airports") shown beside the search bar. */
export const CountChip: React.FC<CountChipProps> = ({ loading, count, label }) => {
  // While loading, render a same-height skeleton instead of null so the slot
  // keeps its space and nothing on the row shifts when the count arrives. Width
  // is estimated from the label (~7px/char plus the chip padding and the count
  // digits) so the skeleton is close to the chip it is standing in for.
  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={Math.max(96, 24 + (label.length + 8) * 7)}
        height={32}
        sx={{ borderRadius: '16px' }}
      />
    );
  }
  return (
    <Chip
      label={`${count.toLocaleString()} ${label}`}
      size="medium"
      sx={{
        height: 32,
        fontSize: '0.8125rem',
        fontWeight: 600,
        px: 0.5,
        border: 'none',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        color: 'primary.dark',
      }}
    />
  );
};

export interface SearchToolbarProps {
  placeholder: string;
  /** The value being typed (not yet applied). */
  inputValue: string;
  onInputChange: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown: React.KeyboardEventHandler;
  onSubmit: () => void;
  onClear: () => void;
  matchMode: SearchMatchMode;
  onMatchModeChange: (mode: SearchMatchMode) => void;
  /** The term currently applied to the list — drives the status hint. */
  activeSearch: string;
  hasPendingInput: boolean;
  /**
   * Optional record-count content, rendered between the search field and the
   * match toggle. Sits after the flex-grow text field so the field absorbs its
   * width (including load → loaded) and the toggle / Search button never shift.
   */
  countSlot?: React.ReactNode;
  /** Optional trailing content on the controls row (e.g. a country filter). */
  rightSlot?: React.ReactNode;
}

// Every control on the search row is pinned to this height so the text field,
// match toggle and Search button share one baseline. The theme renders the
// outlined input at ~50px (padding 14px + 0.95rem font), so we pin the input
// root too — otherwise the toggle/button (previously hard-set to 56) sat taller
// than the search bar.
const CONTROL_HEIGHT = 48;

/**
 * The Data Management search controls: a text field that only applies its value
 * on Enter / Search, plus an Exact-vs-Partial match toggle.
 */
export const SearchToolbar: React.FC<SearchToolbarProps> = ({
  placeholder,
  inputValue,
  onInputChange,
  onKeyDown,
  onSubmit,
  onClear,
  matchMode,
  onMatchModeChange,
  activeSearch,
  hasPendingInput,
  countSlot,
  rightSlot,
}) => {
  let hint: string | null = null;
  if (hasPendingInput && inputValue.trim()) {
    hint = 'Press Enter or click Search to apply.';
  } else if (activeSearch) {
    hint = matchMode === 'exact'
      ? `Showing exact matches for "${activeSearch}".`
      : `Showing entries containing "${activeSearch}".`;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder={placeholder}
          value={inputValue}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          sx={{
            flex: 1,
            minWidth: 240,
            margin: 0,
            // Pin the input to the shared control height and drop the theme's
            // vertical padding — the input root is a centered flex box, so the
            // text stays vertically centred while the box matches the toggle/button.
            '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT },
            '& .MuiOutlinedInput-input': { paddingTop: 0, paddingBottom: 0 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (inputValue || activeSearch) ? (
              <InputAdornment position="end">
                <Tooltip title="Clear search">
                  <IconButton size="small" onClick={onClear} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
          }}
        />
        {countSlot && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {countSlot}
          </Box>
        )}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={matchMode}
          onChange={(_event, mode) => mode && onMatchModeChange(mode as SearchMatchMode)}
          aria-label="Match mode"
          sx={{
            height: CONTROL_HEIGHT,
            '& .MuiToggleButton-root': { height: CONTROL_HEIGHT, py: 0, px: 2 },
          }}
        >
          <Tooltip title="Match the whole value (case-insensitive)">
            <ToggleButton value="exact" aria-label="Exact match">Exact</ToggleButton>
          </Tooltip>
          <Tooltip title="Match anywhere inside the value">
            <ToggleButton value="contains" aria-label="Partial match">Partial</ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={onSubmit}
          sx={{ height: CONTROL_HEIGHT, px: 3 }}
        >
          Search
        </Button>
        {rightSlot && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              // Pin right-slot controls (e.g. the country filter Select) to the
              // shared row height too — the theme's 14px input padding renders
              // them ~5px taller than the field/toggle/button otherwise.
              '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT },
              '& .MuiSelect-select': { paddingTop: 0, paddingBottom: 0 },
            }}
          >
            {rightSlot}
          </Box>
        )}
      </Box>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

/** SearchToolbar wrapped in the standard Paper used above every CRUD table. */
export const SearchBarPaper: React.FC<SearchToolbarProps> = (props) => (
  <Paper sx={{ p: 2, mb: 3 }}>
    <SearchToolbar {...props} />
  </Paper>
);

interface CrudTablePaginationProps {
  loading: boolean;
  totalCount: number;
  rowsPerPage: number;
  page: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: React.ChangeEventHandler<HTMLInputElement>;
}

/** TablePagination block shown below every CRUD table (hidden while loading). */
export const CrudTablePagination: React.FC<CrudTablePaginationProps> = ({
  loading,
  totalCount,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
}) => {
  if (loading) return null;
  return (
    <TablePagination
      rowsPerPageOptions={[10, 20, 50, 100]}
      component="div"
      count={totalCount}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
};

interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  children: React.ReactNode;
}

/** Standard "Confirm Delete" dialog used by every CRUD page. */
export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  children,
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Confirm Delete</DialogTitle>
    <DialogContent>
      <Typography>{children}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="outlined">Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

/** Skeleton action cell — two circular skeleton buttons side by side. */
export const SkeletonActionCell: React.FC = () => (
  <TableCell align="center">
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
      <Skeleton variant="circular" width={28} height={28} />
      <Skeleton variant="circular" width={28} height={28} />
    </Box>
  </TableCell>
);

interface PortCodeSkeletonRowProps {
  /** Row index — used to vary the name-column skeleton width. */
  idx: number;
  /** Width of the leading code-chip skeleton (48 for IATA, 62 for UN/LOCODE). */
  codeWidth: number;
}

/**
 * A single skeleton table row for the port-style tables (AirportsPage,
 * SeaportsPage): code chip, name, city, country, coordinates, actions.
 */
export const PortCodeSkeletonRow: React.FC<PortCodeSkeletonRowProps> = ({ idx, codeWidth }) => (
  <TableRow key={`skeleton-${idx}`}>
    <TableCell><Skeleton variant="rounded" width={codeWidth} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
    <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 35)}%`} /></TableCell>
    <TableCell><Skeleton variant="text" width={100} /></TableCell>
    <TableCell><Skeleton variant="text" width={120} /></TableCell>
    <TableCell><Skeleton variant="text" width={130} sx={{ fontSize: '0.75rem' }} /></TableCell>
    <SkeletonActionCell />
  </TableRow>
);

interface EditDeleteActionsProps {
  onEdit: () => void;
  onDelete: () => void;
  /** Optional MUI color applied to the Edit IconButton (e.g. 'primary'). */
  editColor?: React.ComponentProps<typeof IconButton>['color'];
}

/**
 * The Edit + Delete action cell used identically by every CRUD table row.
 * Renders a `<TableCell align="center">` with Edit and Delete icon buttons.
 */
export const EditDeleteActions: React.FC<EditDeleteActionsProps> = ({
  onEdit,
  onDelete,
  editColor,
}) => (
  <TableCell align="center">
    <Tooltip title="Edit">
      <IconButton
        size="small"
        onClick={onEdit}
        color={editColor}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Delete">
      <IconButton
        size="small"
        onClick={onDelete}
        color="error"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </TableCell>
);

interface NoResultsRowProps {
  colSpan: number;
  /** The search term currently applied, if any. */
  search: string;
  matchMode: SearchMatchMode;
}

/** Empty-state row — tells the user when an exact search can be widened. */
export const NoResultsRow: React.FC<NoResultsRowProps> = ({ colSpan, search, matchMode }) => {
  let message = 'No records found.';
  if (search && matchMode === 'exact') {
    message = `No exact match for "${search}". Switch to "Partial" to search inside values.`;
  } else if (search) {
    message = `No entry contains "${search}".`;
  }
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
        <Typography variant="body2" color="text.secondary">{message}</Typography>
      </TableCell>
    </TableRow>
  );
};

interface CoordinatesCaptionProps {
  lat: number | null | undefined;
  lon: number | null | undefined;
}

/**
 * The coordinates table cell content (lat, lon to 4 decimals) shared by
 * AirportsPage, SeaportsPage and PostalCodesPage.
 */
export const CoordinatesCaption: React.FC<CoordinatesCaptionProps> = ({ lat, lon }) => (
  <Typography variant="caption">
    {formatCoord4(lat)},{' '}{formatCoord4(lon)}
  </Typography>
);

// ── Decimal input ────────────────────────────────────────────────────────────

/** Rewrite a typed decimal to dot notation, dropping any thousands separators. */
export const toDotDecimal = (raw: string): string => raw.replace(/,/g, '.');

const decimalToText = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  return String(value);
};

/**
 * Validates one coordinate/decimal form value. Returns an error message, or
 * `undefined` when the value is a usable finite number.
 */
export const validateDecimal = (
  value: number | string | null | undefined,
  label: string
): string | undefined => {
  if (value === null || value === undefined || value === '') return `${label} is required`;
  return Number.isFinite(Number(toDotDecimal(String(value))))
    ? undefined
    : `${label} must be a number using "." as the decimal separator`;
};

/**
 * `true` when `text` still represents `value`, so a half-typed entry ("47.",
 * "-") is not rewritten by the re-sync effect below.
 */
const representsSameNumber = (text: string, incoming: string): boolean => {
  if (text === incoming) return true;
  const typed = Number.parseFloat(text);
  const current = Number.parseFloat(incoming);
  if (Number.isNaN(typed) && Number.isNaN(current)) return true;
  return typed === current;
};

type DecimalFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number | string | null | undefined;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
};

/**
 * Decimal input that always shows and emits a dot decimal separator.
 *
 * `<input type="number">` renders its value with the *browser locale's*
 * separator, so a stored 10.809690 shows up as "10,809690" for anyone on a
 * comma locale. This is a text input instead, so the value round-trips
 * verbatim; a comma typed or pasted by the user is normalised to a dot.
 */
export const DecimalTextField: React.FC<DecimalFieldProps> = ({
  value,
  onChange,
  inputProps,
  ...rest
}) => {
  const [text, setText] = useState(() => decimalToText(value));

  useEffect(() => {
    const incoming = decimalToText(value);
    setText(prev => (representsSameNumber(prev, incoming) ? prev : incoming));
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = toDotDecimal(event.target.value);
    setText(normalized);
    if (normalized !== event.target.value) event.target.value = normalized;
    onChange(event);
  };

  return (
    <TextField
      {...rest}
      type="text"
      value={text}
      onChange={handleChange}
      inputProps={{ inputMode: 'decimal', ...inputProps }}
    />
  );
};

// ── Shared lat/lon form-field components ─────────────────────────────────────

type LatLonFieldValue = number | string | undefined;

interface LatLonFieldsProps {
  /** Grid xs width for each field (default 6). */
  xs?: number;
  latValue: LatLonFieldValue;
  lonValue: LatLonFieldValue;
  onLatChange: React.ChangeEventHandler<HTMLInputElement>;
  onLonChange: React.ChangeEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  latError?: string;
  lonError?: string;
}

/**
 * Two Grid items (Latitude + Longitude) sharing the same layout.
 * Must be rendered inside a `<Grid container>`.
 */
export const LatLonFields: React.FC<LatLonFieldsProps> = ({
  xs = 6,
  latValue,
  lonValue,
  onLatChange,
  onLonChange,
  onPaste,
  latError,
  lonError,
}) => (
  <>
    <Grid item xs={xs}>
      <DecimalTextField
        fullWidth
        label="Latitude"
        value={latValue}
        onChange={onLatChange}
        onPaste={onPaste}
        required
        error={!!latError}
        helperText={latError}
      />
    </Grid>
    <Grid item xs={xs}>
      <DecimalTextField
        fullWidth
        label="Longitude"
        value={lonValue}
        onChange={onLonChange}
        onPaste={onPaste}
        required
        error={!!lonError}
        helperText={lonError}
      />
    </Grid>
  </>
);

/** A coordinate value as it flows through the form: numeric, string-in-progress, or unset. */
type CoordinateValue = number | string | undefined;

interface PortSharedFormFieldsProps {
  /** Displayed name value */
  nameValue: string | undefined;
  cityValue: string | undefined;
  countryValue: string | undefined;
  countryNameValue: string | undefined;
  latValue: CoordinateValue;
  lonValue: CoordinateValue;
  regionValue: string | undefined;
  nameLabel: string;
  onNameChange: React.ChangeEventHandler<HTMLInputElement>;
  onCityChange: React.ChangeEventHandler<HTMLInputElement>;
  onCountryChange: React.ChangeEventHandler<HTMLInputElement>;
  onCountryNameChange: React.ChangeEventHandler<HTMLInputElement>;
  onLatChange: React.ChangeEventHandler<HTMLInputElement>;
  onLonChange: React.ChangeEventHandler<HTMLInputElement>;
  onRegionChange: React.ChangeEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  nameError?: string;
  latError?: string;
  lonError?: string;
}

/**
 * The shared form field block (Name, City, Country Code, Country Name,
 * Lat/Lon, Region) used identically by AirportsPage and SeaportsPage.
 * Must be rendered inside a `<Grid container>`.
 */
export const PortSharedFormFields: React.FC<PortSharedFormFieldsProps> = ({
  nameValue,
  cityValue,
  countryValue,
  countryNameValue,
  latValue,
  lonValue,
  regionValue,
  nameLabel,
  onNameChange,
  onCityChange,
  onCountryChange,
  onCountryNameChange,
  onLatChange,
  onLonChange,
  onRegionChange,
  onPaste,
  nameError,
  latError,
  lonError,
}) => (
  <>
    <Grid item xs={6}>
      <TextField
        fullWidth
        label={nameLabel}
        value={nameValue}
        onChange={onNameChange}
        required
        error={!!nameError}
        helperText={nameError}
      />
    </Grid>
    <Grid item xs={6}>
      <TextField
        fullWidth
        label="City"
        value={cityValue}
        onChange={onCityChange}
      />
    </Grid>
    <Grid item xs={3}>
      <TextField
        fullWidth
        label="Country Code"
        value={countryValue}
        onChange={onCountryChange}
      />
    </Grid>
    <Grid item xs={3}>
      <TextField
        fullWidth
        label="Country Name"
        value={countryNameValue}
        onChange={onCountryNameChange}
      />
    </Grid>
    <LatLonFields
      xs={4}
      latValue={latValue}
      lonValue={lonValue}
      onLatChange={onLatChange}
      onLonChange={onLonChange}
      onPaste={onPaste}
      latError={latError}
      lonError={lonError}
    />
    <Grid item xs={4}>
      <TextField
        fullWidth
        label="Region"
        value={regionValue}
        onChange={onRegionChange}
      />
    </Grid>
  </>
);

// ── Shared form-change handler factories ─────────────────────────────────────

/**
 * Factory for the standard lat/lon form-change handler used by pages that
 * store lat/lon as `number | undefined` in their form state.
 *
 * Fields listed in `numericFields` are coerced to float (or `undefined` when
 * the input is empty); all other fields receive the raw string value.
 * On every change the corresponding entry in `formErrors` is cleared.
 */
export function makeLatLonFormChangeHandler<T extends Record<string, any>>(
  setFormData: React.Dispatch<React.SetStateAction<T>>,
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  numericFields: ReadonlyArray<keyof T> = ['lat', 'lon'] as ReadonlyArray<keyof T>
) {
  return (field: keyof T) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let nextValue: string | number | undefined = value;
    if (numericFields.includes(field)) {
      const parsed = Number.parseFloat(toDotDecimal(value));
      nextValue = Number.isNaN(parsed) ? undefined : parsed;
    }
    setFormData(prev => ({
      ...prev,
      [field]: nextValue,
    }));

    setFormErrors(prev => {
      if (!prev[field as string]) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };
}

/**
 * Factory for the simpler string-only form-change handler used by pages that
 * store all fields as strings in their form state (Addresses, Cities).
 * On every change the corresponding entry in `formErrors` is cleared.
 */
export function makeSimpleFormChangeHandler<T extends Record<string, any>>(
  setFormData: React.Dispatch<React.SetStateAction<T>>,
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) {
  return (field: keyof T) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    setFormErrors(prev => {
      if (!prev[field as string]) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };
}

// ── Shared CRUD form dialog shell ────────────────────────────────────────────

interface CrudFormDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  saveLabel: string;
  /** Extra props applied to DialogActions > Save button (e.g. disabled, size). */
  saveButtonProps?: React.ComponentProps<typeof Button>;
  /** Extra props applied to DialogActions > Cancel button (e.g. size). */
  cancelButtonProps?: React.ComponentProps<typeof Button>;
  children: React.ReactNode;
}

/**
 * Generic add/edit Dialog shell shared by every CRUD page.
 * Renders: DialogTitle, DialogContent wrapping children, and a
 * Cancel + configurable-label Save button row in DialogActions.
 */
export const CrudFormDialog: React.FC<CrudFormDialogProps> = ({
  open,
  title,
  onClose,
  onSave,
  saveLabel,
  saveButtonProps = {},
  cancelButtonProps = {},
  children,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{children}</DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="outlined" {...cancelButtonProps}>
        Cancel
      </Button>
      <Button onClick={onSave} variant="contained" {...saveButtonProps}>
        {saveLabel}
      </Button>
    </DialogActions>
  </Dialog>
);
